import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Bookmark, MapPin } from "lucide-react";

/**
 * Address autocomplete tied to the address_book API.
 *
 * Props:
 * - value: {name, address, zip, city, country}
 * - onChange(value): called with the updated block
 * - kind: "pickup" | "delivery"
 * - customerId: optional — admin passes the selected customer to filter
 */
export default function AddressAutocomplete({ value, onChange, kind, customerId, testidPrefix = "addr" }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [active, setActive] = useState(0);
  const blurTimer = useRef(null);
  const nameRef = useRef(null);

  // Search as user types in the "name" field
  useEffect(() => {
    const q = value?.name?.trim();
    if (!q || q.length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const params = { q, kind };
        if (customerId) params.customer_id = customerId;
        const { data } = await api.get("/address-book", { params });
        setSuggestions(data);
        setActive(0);
        if (data.length > 0) setOpen(true);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.name, kind, customerId]);

  const apply = (entry) => {
    onChange({
      name: entry.name,
      address: entry.address,
      zip: entry.zip || "",
      city: entry.city || "",
      country: entry.country || "CH",
    });
    setOpen(false);
    setSuggestions([]);
  };

  const setField = (field, val) => {
    onChange({ ...(value || {}), [field]: val });
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      apply(suggestions[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <label className="label-eyebrow block mb-2">Name / Firma *</label>
        <div className="relative">
          <input
            ref={nameRef}
            required
            data-testid={`${testidPrefix}-name`}
            className="input-base"
            value={value?.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            onFocus={() => { if (suggestions.length) setOpen(true); }}
            onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
            onKeyDown={handleKey}
            placeholder='Tippen Sie z. B. "Digitec"…'
            autoComplete="off"
          />
          {value?.name && suggestions.length > 0 && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-[var(--text-3)]">
              {suggestions.length} Treffer
            </div>
          )}
        </div>

        {open && suggestions.length > 0 && (
          <div
            data-testid={`${testidPrefix}-suggestions`}
            onMouseDown={() => { if (blurTimer.current) clearTimeout(blurTimer.current); }}
            className="absolute z-30 left-0 right-0 mt-1 card max-h-72 overflow-auto shadow-2xl"
          >
            {suggestions.map((s, idx) => (
              <button
                type="button"
                key={s.id}
                onClick={() => apply(s)}
                data-testid={`${testidPrefix}-suggestion-${idx}`}
                onMouseEnter={() => setActive(idx)}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-3 border-b border-[var(--border)] last:border-b-0 ${
                  idx === active ? "bg-[#D4AF37]/10" : "hover:bg-[var(--surface-2)]"
                }`}
              >
                <Bookmark size={14} strokeWidth={1.5} className="text-[#D4AF37] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--text)] truncate">{s.name}</div>
                  <div className="text-xs text-[var(--text-2)] truncate">
                    {s.address}{s.zip || s.city ? `, ${s.zip || ""} ${s.city || ""}`.replace("  "," ").trim() : ""}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[var(--text-3)] shrink-0">{s.use_count}×</span>
              </button>
            ))}
            <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[var(--text-3)] bg-[var(--surface-2)] border-t border-[var(--border)] flex items-center gap-2">
              <MapPin size={11} /> Aus Ihrem Adressbuch · ↑ ↓ Enter zum Übernehmen
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="label-eyebrow block mb-2">Strasse / Hausnummer *</label>
        <input required data-testid={`${testidPrefix}-address`} className="input-base" value={value?.address || ""} onChange={(e) => setField("address", e.target.value)} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label-eyebrow block mb-2">PLZ</label>
          <input data-testid={`${testidPrefix}-zip`} className="input-base" value={value?.zip || ""} onChange={(e) => setField("zip", e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="label-eyebrow block mb-2">Ort</label>
          <input data-testid={`${testidPrefix}-city`} className="input-base" value={value?.city || ""} onChange={(e) => setField("city", e.target.value)} />
        </div>
      </div>
    </div>
  );
}
